import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { C, F } from "@tokens";
import Card from "@components/card";
import SessionsPerDayChart from "@components/charts/SessionsPerDayChart";
import usageAnalyticsService from "../services/usageAnalyticsService";

function formatDuration(totalSeconds) {
  const seconds = Math.round(Number(totalSeconds) || 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

function formatHours(totalSeconds) {
  return `${(Number(totalSeconds) / 3600).toFixed(1)}h`;
}

function formatDayLabel(isoDay) {
  const [year, month, day] = isoDay.split("-");
  return `${day}/${month}/${year}`;
}

const MONTH_LABELS_PT = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

function formatMonthLabel(isoMonth) {
  const [year, month] = isoMonth.split("-");
  return `${MONTH_LABELS_PT[Number(month) - 1]}/${year.slice(2)}`;
}

const EVENT_TYPE_LABEL = {
  tab_view: "Aba visitada",
  filter_applied: "Filtro aplicado",
  action: "Ação",
};

function StatTile({ label, value, sub }) {
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: "16px 18px",
        flex: 1,
        minWidth: 160,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: C.text4,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontFamily: F.text,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: C.text,
          fontFamily: F.display,
          marginTop: 4,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div
      style={{
        fontSize: 14,
        fontWeight: 700,
        color: C.text,
        fontFamily: F.display,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

export default function UsageAnalyticsPage() {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    usageAnalyticsService
      .getOverview({ days: 30, userId: selectedUserId || undefined })
      .then((res) => {
        if (!cancelled) setOverview(res?.data ?? res);
      })
      .catch((err) => {
        console.error("Erro ao carregar estatísticas de uso:", err);
        if (!cancelled)
          setError("Não foi possível carregar as estatísticas de uso.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUserId]);

  const sessionsPerDay = (overview?.sessionsPerDay || []).map((row) => ({
    day: formatDayLabel(row.day),
    sessions: Number(row.sessions),
  }));
  const sessionsPerMonth = (overview?.sessionsPerMonth || []).map((row) => ({
    month: formatMonthLabel(row.month),
    sessions: Number(row.sessions),
    total_duration_seconds: Number(row.total_duration_seconds),
  }));
  const topUsers = overview?.topUsers || [];
  const topEvents = overview?.topEvents || [];
  const selectedUser = overview?.selectedUser;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bg,
        fontFamily: F.text,
        padding: "24px 32px",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Link
          to="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: C.text3,
            textDecoration: "none",
            marginBottom: 16,
          }}
        >
          <ArrowLeft size={14} /> Voltar ao dashboard
        </Link>

        {selectedUserId ? (
          <>
            <button
              onClick={() => setSelectedUserId(null)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                color: C.g700,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                marginBottom: 8,
                fontFamily: F.text,
              }}
            >
              <ArrowLeft size={14} /> Voltar pra visão geral
            </button>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: C.text,
                fontFamily: F.display,
                margin: "0 0 4px",
              }}
            >
              Uso de {selectedUser?.name || selectedUser?.email || "usuário"}
            </h1>
            <p style={{ fontSize: 13, color: C.text3, margin: "0 0 24px" }}>
              {selectedUser?.email} · últimos 30 dias
            </p>
          </>
        ) : (
          <>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: C.text,
                fontFamily: F.display,
                margin: "0 0 4px",
              }}
            >
              Uso da plataforma
            </h1>
            <p style={{ fontSize: 13, color: C.text3, margin: "0 0 24px" }}>
              Últimos 30 dias · todas as empresas
            </p>
          </>
        )}

        {loading && (
          <div style={{ color: C.text3, fontSize: 13 }}>Carregando…</div>
        )}
        {error && <div style={{ color: C.red, fontSize: 13 }}>{error}</div>}

        {!loading && !error && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 24,
                flexWrap: "wrap",
              }}
            >
              <StatTile
                label="Sessões (30 dias)"
                value={overview?.totalSessions ?? 0}
              />
              <StatTile
                label="Duração média de sessão"
                value={formatDuration(overview?.avgSessionDurationSeconds)}
              />
              {!selectedUserId && (
                <StatTile
                  label="Usuários no ranking"
                  value={topUsers.length}
                  sub="top 10 por tempo de uso"
                />
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
                marginBottom: 24,
              }}
            >
              <Card style={{ padding: "18px 20px", flex: 1, minWidth: 340 }}>
                <SectionTitle>Sessões por mês (12 meses)</SectionTitle>
                {sessionsPerMonth.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.text3 }}>
                    Sem sessões registradas no período.
                  </div>
                ) : (
                  <SessionsPerDayChart
                    data={sessionsPerMonth}
                    xKey="month"
                    yKey="sessions"
                    name="Sessões"
                    color={C.g600}
                  />
                )}
              </Card>
              <Card style={{ padding: "18px 20px", flex: 1, minWidth: 340 }}>
                <SectionTitle>Tempo de uso por mês (12 meses)</SectionTitle>
                {sessionsPerMonth.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.text3 }}>
                    Sem sessões registradas no período.
                  </div>
                ) : (
                  <SessionsPerDayChart
                    data={sessionsPerMonth}
                    xKey="month"
                    yKey="total_duration_seconds"
                    name="Tempo de uso"
                    color={C.blue}
                    formatValue={formatHours}
                  />
                )}
              </Card>
            </div>

            <Card style={{ padding: "18px 20px", marginBottom: 24 }}>
              <SectionTitle>Sessões por dia (30 dias)</SectionTitle>
              {sessionsPerDay.length === 0 ? (
                <div style={{ fontSize: 12, color: C.text3 }}>
                  Sem sessões registradas no período.
                </div>
              ) : (
                <SessionsPerDayChart data={sessionsPerDay} />
              )}
            </Card>

            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              {!selectedUserId && (
                <Card style={{ padding: "18px 20px", flex: 1, minWidth: 340 }}>
                  <SectionTitle>Usuários mais ativos</SectionTitle>
                  {topUsers.length === 0 ? (
                    <div style={{ fontSize: 12, color: C.text3 }}>
                      Sem dados no período.
                    </div>
                  ) : (
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            textAlign: "left",
                            color: C.text4,
                            fontSize: 10,
                            textTransform: "uppercase",
                          }}
                        >
                          <th style={{ padding: "6px 8px" }}>Usuário</th>
                          <th style={{ padding: "6px 8px" }}>Sessões</th>
                          <th style={{ padding: "6px 8px" }}>Tempo total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topUsers.map((u) => (
                          <tr
                            key={u.user_id}
                            onClick={() => setSelectedUserId(u.user_id)}
                            title="Ver uso deste usuário"
                            style={{
                              borderTop: `1px solid ${C.border}`,
                              cursor: "pointer",
                            }}
                          >
                            <td style={{ padding: "8px" }}>
                              <div style={{ fontWeight: 600, color: C.text }}>
                                {u.user_name || u.user_email}
                              </div>
                              <div style={{ color: C.text4, fontSize: 11 }}>
                                {u.user_email}
                              </div>
                            </td>
                            <td style={{ padding: "8px", color: C.text2 }}>
                              {u.sessions}
                            </td>
                            <td style={{ padding: "8px", color: C.text2 }}>
                              {formatDuration(u.total_duration_seconds)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
              )}

              <Card style={{ padding: "18px 20px", flex: 1, minWidth: 340 }}>
                <SectionTitle>Eventos mais frequentes</SectionTitle>
                {topEvents.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.text3 }}>
                    Sem dados no período.
                  </div>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          textAlign: "left",
                          color: C.text4,
                          fontSize: 10,
                          textTransform: "uppercase",
                        }}
                      >
                        <th style={{ padding: "6px 8px" }}>Tipo</th>
                        <th style={{ padding: "6px 8px" }}>Alvo</th>
                        <th style={{ padding: "6px 8px" }}>Ocorrências</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topEvents.map((e, i) => (
                        <tr
                          key={`${e.event_type}-${e.event_target}-${i}`}
                          style={{ borderTop: `1px solid ${C.border}` }}
                        >
                          <td style={{ padding: "8px", color: C.text2 }}>
                            {EVENT_TYPE_LABEL[e.event_type] || e.event_type}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              fontWeight: 600,
                              color: C.text,
                            }}
                          >
                            {e.event_target}
                          </td>
                          <td style={{ padding: "8px", color: C.text2 }}>
                            {e.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
